import React, { useState, useEffect, useRef } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { Menu, X, Bell, ChevronDown, User, LogOut, ArrowLeft, Lock } from 'lucide-react';
import ResumeUploadModal from "../../resumetemplate/ResumeUploadModal";
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
  DropdownMenuLabel,
} from '@/components/ui/dropdown-menu';
import { Avatar, AvatarImage, AvatarFallback } from '@/components/ui/avatar';
import { Container } from '@/components/ui/container';
import { cn } from '@/lib/utils';
import { getColor } from '@/config/theme';

// Notification bell + dropdown. Keeps its OWN open-state so multiple instances
// (desktop nav + mobile menu) stay independent. With a shared open flag, Radix
// portals BOTH dropdowns at once — and the CSS-hidden mobile one, having no
// anchor, lands in the top-left corner (the "double notification" bug).
const NotificationBell = ({ notifications = [], unreadCount = 0, onSelect }) => {
  const [open, setOpen] = useState(false);
  return (
    <div className="relative mr-2">
      <DropdownMenu open={open} onOpenChange={setOpen}>
        <DropdownMenuTrigger asChild>
          <Button
            variant="ghost"
            size="icon"
            className="relative h-10 w-10"
            aria-label="Notifications"
          >
            <Bell className="h-5 w-5 text-foreground" />
            {unreadCount > 0 && (
              <span
                className="absolute top-1.5 right-1.5 h-2.5 w-2.5 rounded-full bg-destructive border-2 border-background"
                aria-hidden
              />
            )}
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-80 md:w-96">
          <DropdownMenuLabel className="flex items-center justify-between">
            <span>Notifications</span>
            <span className="text-xs font-normal text-muted-foreground">
              {unreadCount > 0 ? `${unreadCount} new` : "No new"}
            </span>
          </DropdownMenuLabel>
          <DropdownMenuSeparator />
          <div className="max-h-[300px] overflow-y-auto">
            {notifications.length === 0 && (
              <div className="px-2 py-4 text-sm text-muted-foreground text-center">
                You're all caught up.
              </div>
            )}
            {notifications.map((n) => (
              <DropdownMenuItem
                key={n.id}
                onClick={() => onSelect?.(n.id)}
                className="flex gap-3 p-3 cursor-pointer focus:bg-accent"
              >
                <div className="flex-shrink-0">
                  <div className="h-10 w-10 rounded-full bg-muted flex items-center justify-center font-semibold text-sm">
                    {n.company_name ? (n.company_name.charAt(0) || "C") : "C"}
                  </div>
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-start justify-between gap-2 mb-1">
                    <div className="font-semibold text-sm">{n.job_title ?? "Questionnaire"}</div>
                    <div className="text-xs text-muted-foreground whitespace-nowrap">
                      {n.created_at ? new Date(n.created_at).toLocaleDateString() : ""}
                    </div>
                  </div>
                  <div className="text-xs text-muted-foreground line-clamp-2">
                    {n.message ? (n.message.length > 120 ? `${n.message.slice(0, 120)}…` : n.message) : `You have a questionnaire from ${n.company_name}.`}
                  </div>
                  {!n.read && (
                    <span className="inline-block mt-2 text-xs text-destructive-foreground bg-destructive px-2 py-0.5 rounded-full">
                      New
                    </span>
                  )}
                </div>
              </DropdownMenuItem>
            ))}
          </div>
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
};

const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const [showToolsDropdown, setShowToolsDropdown] = useState(false);
  const [showProfileDropdown, setShowProfileDropdown] = useState(false);
  const [showResumeTemplatesModal, setShowResumeTemplatesModal] = useState(false);

  // Notification state
  const [notifications, setNotifications] = useState([]); // {id, questionnaire_id, company_name, job_title, message, created_at, read}
  const [unreadCount, setUnreadCount] = useState(0);
  const [toastMessage, setToastMessage] = useState(null);

  const [profile, setProfile] = useState({
    firstName: "",
    photoUrl: null, // url or data-url
    loading: true,
  });

  const navigate = useNavigate();
  const loc = useLocation();


  // Toggle Mobile Menu
  const toggleMenu = () => {
    setIsOpen(!isOpen);
  };

  // Auth checks
  const userEmail = localStorage.getItem("user_email");
  const companyEmail = localStorage.getItem("company_email");
  const adminEmail = localStorage.getItem("admin_email");
  const isSuperAdmin = localStorage.getItem("is_super_admin") === "true";
  const isAuthenticated = !!(userEmail || companyEmail || adminEmail);

  // --- Fetch profile (unchanged) ---
  useEffect(() => {
    if (!userEmail) {
      setProfile((p) => ({ ...p, loading: false }));
      return;
    }

    const controller = new AbortController();
    const signal = controller.signal;

    fetch("/api/get_user_profile_picture", {
      method: "GET",
    })
      .then(async (res) => {
        if (signal.aborted) return;
        if (res.status === 401) {
          setProfile({ firstName: "", photoUrl: null, loading: false });
          return;
        }
        if (!res.ok) {
          setProfile((p) => ({ ...p, loading: false }));
          return;
        }
        const data = await res.json();
        const firstName = data.full_name;
        const rawPhoto = data.profile_photo || null;

        let photoUrl = null;
        if (rawPhoto && typeof rawPhoto === "string") {
          if (rawPhoto.startsWith("data:") || rawPhoto.startsWith("http") || rawPhoto.startsWith("/")) {
            photoUrl = rawPhoto;
          } else {
            photoUrl = `/${rawPhoto.replace(/^\/+/, "")}`;
          }
        }

        setProfile({ firstName, photoUrl, loading: false });
      })
      .catch((err) => {
        if (err.name === "AbortError") return;
        console.error("Failed to fetch profile:", err);
        setProfile((p) => ({ ...p, loading: false }));
      });

    return () => controller.abort();
  }, [userEmail, loc.pathname]);

  // --- Fetch unsubmitted questionnaires (notifications) for user ---
  // useEffect(() => {
  //   if (!userEmail) {
  //     setNotifications([]);
  //     setUnreadCount(0);
  //     return;
  //   }

  //   const controller = new AbortController();
  //   const signal = controller.signal;

  //   async function fetchNotifications() {
  //     try {
  //       // Expect backend to return: { notifications: [...], unread_count: N }
  //       const res = await fetch("/api/questionnaires/unsubmitted", {
  //         method: "GET",
  //         credentials: "same-origin", // if you use cookies/sessions
  //         signal,
  //       });
  //       if (!res.ok) {
  //         console.warn("Failed to fetch unsubmitted questionnaires:", res.status);
  //         return;
  //       }
  //       const data = await res.json();
  //       const items = Array.isArray(data.notifications) ? data.notifications : [];
  //       setNotifications(items);
  //       setUnreadCount(data.unread_count ?? items.filter((n) => !n.read).length);

  //       // If user just signed in and there are notifications, show brief toast
  //       if ((data.unread_count ?? items.filter((n) => !n.read).length) > 0) {
  //         setToastMessage(`You have ${data.unread_count ?? items.length} questionnaire(s) pending. Click the bell to view.`);
  //         // auto-clear after 6s
  //         setTimeout(() => setToastMessage(null), 6000);
  //       }
  //     } catch (err) {
  //       if (err.name === "AbortError") return;
  //       console.error("Error fetching notifications:", err);
  //     }
  //   }

  //   fetchNotifications();

  //   // poll/refresh every 60s while mounted (optional)
  //   const interval = setInterval(fetchNotifications, 60000);

  //   return () => {
  //     controller.abort();
  //     clearInterval(interval);
  //   };
  // }, [userEmail]);

  // Close mobile menu when clicking outside (optional, can be handled by DropdownMenu)

  // Helpers for scrolling sections (same as your code)
  const scrollToFAQ = () => {
    const faqSection = document.getElementById("faq-section");
    if (faqSection) faqSection.scrollIntoView({ behavior: "smooth" });
  };
  const handleFAQClick = () => {
    if (loc.pathname === "/") scrollToFAQ();
    else {
      navigate("/");
      setTimeout(scrollToFAQ, 500);
    }
  };
  const scrollToTest = () => {
    const testSection = document.getElementById("test-section");
    if (testSection) testSection.scrollIntoView({ behavior: "smooth" });
  };
  const handleTestClick = () => {
    if (loc.pathname === "/") scrollToTest();
    else {
      navigate("/");
      setTimeout(scrollToTest, 500);
    }
  };

  // Handle Tools Click (unchanged)
  const handleToolsClick = (toolType) => {
    if (userEmail) {
      if (toolType === "enhance-cv") {
        navigate("/create-resume");
        setTimeout(() => {
          window.scrollTo(0, 0);
        }, 100);
      } else if (toolType === "jd-align-resume") {
        navigate("/create-resume?modal=jd-align");
        setTimeout(() => {
          window.scrollTo(0, 0);
        }, 100);
      } else if (toolType === "resume-templates") {
        setShowResumeTemplatesModal(true);
        setShowToolsDropdown(false);
      } else if (toolType === "cv-templates") {
        navigate("/cv-templates");
        setTimeout(() => {
          window.scrollTo(0, 0);
        }, 100);
        setShowToolsDropdown(false);
      }
    } else {
      localStorage.setItem("intendedDestination", toolType);
      navigate("/login_user");
    }
  };

  // Handle Resume Upload (unchanged)
  const handleUploadResume = (parsedData) => {
    console.log("Parsed resume data:", parsedData);
    localStorage.setItem("parsedResumeData", JSON.stringify(parsedData));
    setShowResumeTemplatesModal(false);
    navigate("/create-resume?view=templates");
  };

  const handleCreateNew = () => {
    setShowResumeTemplatesModal(false);
    navigate("/create-resume?view=templates");
  };

  const handleCloseModal = () => {
    setShowResumeTemplatesModal(false);
  };

  // Logout (unchanged)
  const handleLogout = () => {
    localStorage.removeItem("isUserAuthenticated");
    localStorage.removeItem("isCompanyAuthenticated");
    localStorage.removeItem("isAdminAuthenticated");
    localStorage.removeItem("user_email");
    localStorage.removeItem("company_email");
    localStorage.removeItem("admin_email");
    localStorage.removeItem("admin_id");
    localStorage.removeItem("is_super_admin");
    localStorage.removeItem("auth_token");
    localStorage.removeItem("loginTimestamp");
    window.location.replace("/");
  };

  // My profile click for user (unchanged)
  const goToProfile = () => {
    if (adminEmail) navigate("/company-admin-home");
    else if (companyEmail) navigate("/companyhome");
    else if (userEmail) navigate("/crr");
    else navigate("/login_user");
    setShowProfileDropdown(false);
  };

  const goHome = () => {
    if (adminEmail) navigate("/company-admin-home");
    else if (companyEmail) navigate("/companyhome");
    else if (userEmail) navigate("/user-home");
    else navigate("/");
  };

  // Admin display name & initial helpers (unchanged)
  const getAdminDisplayName = () => {
    if (adminEmail) {
      const name = adminEmail.split("@")[0];
      return name.split(".").map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(" ");
    }
    return "Admin";
  };
  const getInitial = () => {
    if (adminEmail) {
      return getAdminDisplayName().charAt(0).toUpperCase();
    }
    if (!profile.firstName) return "?";
    const firstWord = profile.firstName.trim().split(/\s+/)[0] || "";
    return firstWord.charAt(0).toUpperCase() || "?";
  };

  useEffect(() => {
    if (!userEmail) {
      setNotifications([]);
      setUnreadCount(0);
      return;
    }
  
    let cancelled = false;
    const fetchNotifications = async () => {
      try {
        const res = await fetch("/api/questionnaires/unsubmitted", {
          method: "GET",
          credentials: "same-origin", // use cookies/session
          headers: { "Accept": "application/json" },
        });
        if (!res.ok) {
          console.warn("Failed to fetch notifications:", res.status);
          return;
        }
        const data = await res.json();
        if (cancelled) return;
  
        // Ensure every notification has the fields your UI expects
        const items = (data.notifications || []).map(n => ({
          id: n.id || n.questionnaire_id,
          questionnaire_id: n.questionnaire_id,
          job_title: n.job_title || "Questionnaire",
          company_name: n.company_name || "Our Company",
          message: n.message || `You have a questionnaire from ${n.company_name || "the company"}.`,
          created_at: n.created_at || new Date().toISOString(),
          read: !!n.read
        }));
  
        setNotifications(items);
        setUnreadCount(typeof data.unread_count === "number" ? data.unread_count : items.filter(i => !i.read).length);
  
        // Optional: show a small toast when new at sign-in
        if (items.length > 0) {
          // your toast logic
          setToastMessage(`You have ${items.filter(i=>!i.read).length} questionnaire(s) pending.`);
          setTimeout(() => setToastMessage(null), 6000);
        }
  
      } catch (err) {
        if (err.name === "AbortError") return;
        console.error("Error fetching questionnaires:", err);
      }
    };
  
    fetchNotifications();

    return () => {
      cancelled = true;
    };
  }, [userEmail]);

  // Mark a single notification as read (backend call optional)
  // const markAsRead = async (notifId) => {
  //   try {
  //     // best-effort backend call; adapt endpoint to your API
  //     await fetch("/api/questionnaires/mark_read", {
  //       method: "POST",
  //       headers: { "Content-Type": "application/json" },
  //       body: JSON.stringify({ notification_id: notifId }),
  //       credentials: "same-origin",
  //     });
  //   } catch (err) {
  //     console.warn("markAsRead error", err);
  //   }
  //   // update local state
  //   setNotifications((prev) => prev.map(n => n.id === notifId ? { ...n, read: true } : n));
  //   setUnreadCount((c) => Math.max(0, c - 1));
  // };

  // When user clicks a notification list item (show detail)
  // const openNotification = (notif) => {
  //   setSelectedNotification(notif);
  //   // if (!notif.read) markAsRead(notif.id);
  // };

  // Redirect to questionnaire form
  const goToQuestionnaire = (questionnaire_id) => {
    // build path the user asked for
    navigate(`/questionnaire?id=${encodeURIComponent(questionnaire_id)}`);
    // (the dropdown closes itself when a menu item is selected)
  };

  // NotificationBell is defined at module scope (above) so each render site
  // owns its open-state — see the comment there for why.

  return (
    <nav className={cn(
      "sticky top-0 z-50 bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60",
      "border-b border-border shadow-sm"
    )}>
      <Container>
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <div className="flex-shrink-0">
            <img
              src="/Logo.png"
              alt="Logo"
              onClick={goHome}
              className="h-10 w-auto cursor-pointer object-contain"
            />
          </div>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center gap-6">
            <button
              onClick={goHome}
              className="text-sm font-medium text-foreground hover:text-primary transition-colors"
            >
              Home
            </button>

            {/* Company Job Dropdown */}
            {companyEmail && (
              <DropdownMenu open={showDropdown} onOpenChange={setShowDropdown}>
                <DropdownMenuTrigger asChild>
                  <Button
                    variant="ghost"
                    className="text-sm font-medium text-foreground hover:text-primary transition-colors"
                  >
                    Job <ChevronDown className="ml-1 h-4 w-4" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="start">
                  <DropdownMenuItem onClick={() => navigate("/ch")}>
                    My Jobs
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate("/jd")}>
                    Post Job
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate("/")}>
                    Tagged Candidates
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate("/")}>
                    Shortlisted/Saved Candidates
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            )}

            {/* Tools Dropdown */}
            {(userEmail || !isAuthenticated) && (
              <DropdownMenu open={showToolsDropdown} onOpenChange={setShowToolsDropdown}>
                <DropdownMenuTrigger asChild>
                  <Button
                    variant="ghost"
                    className="text-sm font-medium text-foreground hover:text-primary transition-colors"
                  >
                    Tools <ChevronDown className="ml-1 h-4 w-4" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="start">
                  <DropdownMenuItem onClick={() => handleToolsClick("enhance-cv")}>
                    Enhance CV
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => handleToolsClick("jd-align-resume")}>
                    Tailor Resume to JD
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => handleToolsClick("resume-templates")}>
                    Resume Templates
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => handleToolsClick("cv-templates")}>
                    CV Templates
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            )}

            <button
              onClick={() => navigate("/aboutus")}
              className="text-sm font-medium text-foreground hover:text-primary transition-colors"
            >
              About
            </button>
            <button
              onClick={handleFAQClick}
              className="text-sm font-medium text-foreground hover:text-primary transition-colors"
            >
              FAQ
            </button>
            <button
              onClick={handleTestClick}
              className="text-sm font-medium text-foreground hover:text-primary transition-colors"
            >
              Testimonial
            </button>
            <button
              onClick={() => navigate("/contactus")}
              className="text-sm font-medium text-foreground hover:text-primary transition-colors"
            >
              Contact
            </button>
          </div>

          {/* Desktop Auth Actions */}
          <div className="hidden md:flex items-center gap-3">
            {isAuthenticated ? (
              <>
                {/* Show notification bell for user accounts only */}
                {userEmail && <NotificationBell notifications={notifications} unreadCount={unreadCount} onSelect={goToQuestionnaire} />}

                {adminEmail ? (
                  <DropdownMenu open={showProfileDropdown} onOpenChange={setShowProfileDropdown}>
                    <DropdownMenuTrigger asChild>
                      <Button
                        variant="ghost"
                        className="h-10 w-10 rounded-full p-0"
                        style={{
                          backgroundColor: getColor('primary'),
                          color: getColor('primaryForeground'),
                        }}
                      >
                        <span className="font-bold text-sm">{getInitial()}</span>
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="w-56">
                      <div className="px-2 py-1.5 border-b border-border">
                        <div className="font-semibold text-sm">{getAdminDisplayName()}</div>
                        <div className="text-xs text-muted-foreground mt-0.5">
                          {isSuperAdmin ? 'Super Admin' : 'Admin'}
                        </div>
                        <div className="text-xs text-muted-foreground mt-0.5 truncate">
                          {adminEmail}
                        </div>
                      </div>
                      <DropdownMenuItem onClick={goToProfile}>
                        Admin Dashboard
                      </DropdownMenuItem>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem
                        onClick={handleLogout}
                        className="text-destructive focus:text-destructive focus:bg-destructive/10"
                      >
                        <LogOut className="mr-2 h-4 w-4" />
                        Logout
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                ) : userEmail ? (
                  <DropdownMenu open={showProfileDropdown} onOpenChange={setShowProfileDropdown}>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" className="h-10 w-10 rounded-full p-0">
                        <Avatar className="h-10 w-10">
                          {profile.photoUrl ? (
                            <AvatarImage
                              src={profile.photoUrl}
                              alt="Profile"
                              onError={(e) => {
                                e.currentTarget.onerror = null;
                                setProfile((p) => ({ ...p, photoUrl: null }));
                              }}
                            />
                          ) : null}
                          <AvatarFallback className="bg-muted text-foreground font-semibold">
                            {profile.loading ? "…" : getInitial()}
                          </AvatarFallback>
                        </Avatar>
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="w-56">
                      <DropdownMenuItem onClick={goToProfile}>
                        <User className="mr-2 h-4 w-4" />
                        My Profile
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => { navigate("/user/account"); setShowProfileDropdown(false); }}>
                        <Lock className="mr-2 h-4 w-4" />
                        Password & security
                      </DropdownMenuItem>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem
                        onClick={handleLogout}
                        className="text-destructive focus:text-destructive focus:bg-destructive/10"
                      >
                        <LogOut className="mr-2 h-4 w-4" />
                        Logout
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                ) : (
                  <Button
                    onClick={handleLogout}
                    variant="destructive"
                    size="sm"
                  >
                    Logout
                  </Button>
                )}
              </>
            ) : (
              <div className="flex items-center gap-3">
                <Button
                  onClick={() => navigate("/login_user")}
                  variant="default"
                  size="sm"
                  style={{
                    backgroundColor: getColor('primary'),
                    color: getColor('primaryForeground'),
                  }}
                >
                  Get Started
                </Button>
                <Button
                  onClick={() => navigate("/login")}
                  variant="outline"
                  size="sm"
                >
                  Hire with Us
                </Button>
              </div>
            )}
          </div>

          {/* Mobile Menu Toggle */}
          <button
            className="md:hidden flex items-center justify-center p-2 rounded-md text-foreground hover:bg-accent transition-colors"
            onClick={toggleMenu}
            aria-label="Toggle mobile menu"
            aria-expanded={isOpen}
          >
            {isOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>
        </div>

        {/* Mobile Navigation */}
        <div
          className={cn(
            'md:hidden overflow-hidden transition-all duration-300 ease-in-out border-t border-border',
            isOpen ? 'max-h-screen opacity-100' : 'max-h-0 opacity-0'
          )}
        >
          <div className="flex flex-col gap-4 py-4">
            <button
              onClick={() => { goHome(); toggleMenu(); }}
              className="text-sm font-medium text-foreground hover:text-primary transition-colors text-left py-2"
            >
              Home
            </button>

            {companyEmail && (
              <div className="flex flex-col gap-2">
                <span className="text-sm font-medium text-foreground py-2">Job</span>
                <div className="flex flex-col gap-1 pl-4">
                  <button
                    onClick={() => { navigate("/ch"); toggleMenu(); }}
                    className="text-sm text-muted-foreground hover:text-primary transition-colors text-left py-1.5"
                  >
                    My Jobs
                  </button>
                  <button
                    onClick={() => { navigate("/jd"); toggleMenu(); }}
                    className="text-sm text-muted-foreground hover:text-primary transition-colors text-left py-1.5"
                  >
                    Post Job
                  </button>
                  <button
                    onClick={() => { navigate("/"); toggleMenu(); }}
                    className="text-sm text-muted-foreground hover:text-primary transition-colors text-left py-1.5"
                  >
                    Tagged Candidates
                  </button>
                  <button
                    onClick={() => { navigate("/"); toggleMenu(); }}
                    className="text-sm text-muted-foreground hover:text-primary transition-colors text-left py-1.5"
                  >
                    Shortlisted/Saved Candidates
                  </button>
                </div>
              </div>
            )}

            {(userEmail || !isAuthenticated) && (
              <div className="flex flex-col gap-2">
                <span className="text-sm font-medium text-foreground py-2">Tools</span>
                <div className="flex flex-col gap-1 pl-4">
                  <button
                    onClick={() => { handleToolsClick("enhance-cv"); toggleMenu(); }}
                    className="text-sm text-muted-foreground hover:text-primary transition-colors text-left py-1.5"
                  >
                    Enhance CV
                  </button>
                  <button
                    onClick={() => { handleToolsClick("jd-align-resume"); toggleMenu(); }}
                    className="text-sm text-muted-foreground hover:text-primary transition-colors text-left py-1.5"
                  >
                    Tailor Resume to JD
                  </button>
                  <button
                    onClick={() => { handleToolsClick("resume-templates"); toggleMenu(); }}
                    className="text-sm text-muted-foreground hover:text-primary transition-colors text-left py-1.5"
                  >
                    Resume Templates
                  </button>
                  <button
                    onClick={() => { handleToolsClick("cv-templates"); toggleMenu(); }}
                    className="text-sm text-muted-foreground hover:text-primary transition-colors text-left py-1.5"
                  >
                    CV Templates
                  </button>
                </div>
              </div>
            )}

            <button
              onClick={() => { navigate("/aboutus"); toggleMenu(); }}
              className="text-sm font-medium text-foreground hover:text-primary transition-colors text-left py-2"
            >
              About
            </button>
            <button
              onClick={() => { handleFAQClick(); toggleMenu(); }}
              className="text-sm font-medium text-foreground hover:text-primary transition-colors text-left py-2"
            >
              FAQ
            </button>
            <button
              onClick={() => { handleTestClick(); toggleMenu(); }}
              className="text-sm font-medium text-foreground hover:text-primary transition-colors text-left py-2"
            >
              Testimonial
            </button>
            <button
              onClick={() => { navigate("/contactus"); toggleMenu(); }}
              className="text-sm font-medium text-foreground hover:text-primary transition-colors text-left py-2"
            >
              Contact
            </button>

            {/* Mobile Auth Actions */}
            {isAuthenticated ? (
              <div className="pt-4 border-t border-border">
                {userEmail && (
                  <div className="mb-4">
                    <NotificationBell notifications={notifications} unreadCount={unreadCount} onSelect={goToQuestionnaire} />
                  </div>
                )}
                {adminEmail ? (
                  <div className="space-y-2">
                    <div className="px-2 py-1.5 border border-border rounded-md bg-muted/50">
                      <div className="font-semibold text-sm">{getAdminDisplayName()}</div>
                      <div className="text-xs text-muted-foreground">{adminEmail}</div>
                    </div>
                    <Button
                      onClick={() => { goToProfile(); toggleMenu(); }}
                      variant="ghost"
                      className="w-full justify-start"
                    >
                      Admin Dashboard
                    </Button>
                    <Button
                      onClick={() => { handleLogout(); toggleMenu(); }}
                      variant="destructive"
                      className="w-full"
                    >
                      <LogOut className="mr-2 h-4 w-4" />
                      Logout
                    </Button>
                  </div>
                ) : userEmail ? (
                  <div className="space-y-2">
                    <Button
                      onClick={() => { goToProfile(); toggleMenu(); }}
                      variant="ghost"
                      className="w-full justify-start"
                    >
                      <User className="mr-2 h-4 w-4" />
                      My Profile
                    </Button>
                    <Button
                      onClick={() => { navigate("/user/account"); toggleMenu(); }}
                      variant="ghost"
                      className="w-full justify-start"
                    >
                      <Lock className="mr-2 h-4 w-4" />
                      Password & security
                    </Button>
                    <Button
                      onClick={() => { handleLogout(); toggleMenu(); }}
                      variant="destructive"
                      className="w-full"
                    >
                      <LogOut className="mr-2 h-4 w-4" />
                      Logout
                    </Button>
                  </div>
                ) : (
                  <Button
                    onClick={() => { handleLogout(); toggleMenu(); }}
                    variant="destructive"
                    className="w-full"
                  >
                    Logout
                  </Button>
                )}
              </div>
            ) : (
              <div className="pt-4 border-t border-border space-y-2">
                <Button
                  onClick={() => { navigate("/login_user"); toggleMenu(); }}
                  variant="default"
                  className="w-full"
                  style={{
                    backgroundColor: getColor('primary'),
                    color: getColor('primaryForeground'),
                  }}
                >
                  Get Started
                </Button>
                <Button
                  onClick={() => { navigate("/login"); toggleMenu(); }}
                  variant="outline"
                  className="w-full"
                >
                  Hire with Us
                </Button>
              </div>
            )}
          </div>
        </div>
      </Container>

      {/* Resume Templates Modal - Exact same as resumelandingpage.jsx */}
      {showResumeTemplatesModal && (
        <ResumeUploadModal 
          onUpload={handleUploadResume}
          onCreateNew={handleCreateNew}
          onClose={handleCloseModal}
        />
      )}

      {/* Toast (brief) */}
      {toastMessage && (
        <div className={cn(
          "fixed right-5 bottom-5 bg-foreground text-background",
          "px-4 py-2.5 rounded-lg shadow-lg z-50",
          "text-sm font-medium"
        )}>
          {toastMessage}
        </div>
      )}
    </nav>
  );
};

export default Navbar;